/* Theme-owned rendering; qualification lives exclusively in Laravel. */
(() => {
  'use strict';
  if (window.__merchandisingBadgesLoaded) return;
  const configNode = document.getElementById('merchandising-badges-config');
  let config = {};
  if (configNode) {
    try { config = JSON.parse(configNode.textContent); } catch (_) { return; }
    window.__merchandisingBadgesLoaded = true;
  }
  const selector = '[data-merchandising-badge]';
  const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
  const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
  const numericId = value => /^[1-9][0-9]{0,19}$/.test(String(value));
  const locale = String(config.locale).toLowerCase().startsWith('fr') ? 'fr' : 'en';
  const pending = new Map();
  const rendered = new WeakMap();
  const allowedTypes = ['best_seller', 'selling_fast', 'trending', 'sold', 'popular', 'rating'];
  let asset;
  let scheduled = false;
  let expirationTimer;
  const defaultTypography = {family: 'default', weight: 'default', style: 'normal'};
  let fontCatalog = {};
  const fontFaces = new Map();
  function validFontCatalog(catalog) {
    return object(catalog) && Object.keys(catalog).length <= 12 && Object.entries(catalog).every(([family, font]) =>
      /^[a-z][a-z_]{0,30}$/.test(family) && object(font) && typeof font.label === 'string' && font.label.length <= 60
      && object(font.variants) && Object.keys(font.variants).length > 0 && Object.entries(font.variants).every(([weight, variants]) =>
        ['200', '300', '400', '500', '600', '700', '900'].includes(weight) && object(variants) && own(variants, 'normal')
        && Object.entries(variants).every(([style, url]) => {
          if (!['normal', 'italic'].includes(style) || typeof url !== 'string' || url.length > 2048 || /["'\\\s]/.test(url)) return false;
          try { const parsed = new URL(url); return parsed.protocol === 'https:' && parsed.hostname === 'cdn.shopify.com' && !parsed.port && !parsed.username && !parsed.password
            && /^\/s\/files\/.+\.woff2$/.test(parsed.pathname); } catch (_) { return false; }
        })));
  }
  try {
    const initialFonts = JSON.parse(document.getElementById('merchandising-badge-font-catalog')?.textContent || '{}');
    if (validFontCatalog(initialFonts)) fontCatalog = initialFonts;
  } catch (_) { /* Legacy editor assets retain the original font. */ }
  function validTypography(value, catalog = fontCatalog) {
    return object(value) && typeof value.weight === 'string' && (value.family === 'default'
      ? value.weight === 'default' && value.style === 'normal'
      : own(catalog, value.family) && own(catalog[value.family].variants, value.weight) && own(catalog[value.family].variants[value.weight], value.style));
  }
  function applyTypography(slot, value, catalog) {
    for (const key of ['family', 'weight', 'style']) slot.style.removeProperty('--merchandising-badge-font-' + key);
    if (!validTypography(value, catalog) || value.family === 'default') return;
    const url = catalog[value.family].variants[value.weight][value.style];
    const key = [value.family, value.weight, value.style, url].join(':');
    if (!fontFaces.has(key) && typeof FontFace !== 'undefined' && document.fonts) {
      const name = 'MerchandisingBadgeFont' + (fontFaces.size + 1);
      const face = new FontFace(name, 'url(' + JSON.stringify(url) + ')', {weight: value.weight, style: value.style, display: 'swap'});
      document.fonts.add(face); // The browser downloads only faces actually used by visible text.
      fontFaces.set(key, name);
    }
    slot.style.setProperty('--merchandising-badge-font-family', fontFaces.has(key) ? '"' + fontFaces.get(key) + '", Arial, sans-serif' : 'Arial, sans-serif');
    slot.style.setProperty('--merchandising-badge-font-weight', value.weight);
    slot.style.setProperty('--merchandising-badge-font-style', value.style);
  }
  const usable = data => !data.enabled || !data.ready || data.retention === 'until_replaced' || Date.parse(data.expires_at) > Date.now();
  const icons = {
    trophy: {path: 'M7 3h10v6a5 5 0 0 1-10 0V3ZM7 5H3v3a5 5 0 0 0 5 5m9-8h4v3a5 5 0 0 1-5 5m-4 1v6m-4 1h8'},
    eye: {path: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Zm13 0a3 3 0 1 0-6 0 3 3 0 0 0 6 0Z'},
    star: {path: 'm12 2.5 3 6.1 6.8 1-4.9 4.8 1.2 6.7-6.1-3.2-6.1 3.2 1.2-6.7L2.2 9.6l6.8-1Z', fill: true},
    flame: {path: 'M13 2c1 6-5 6-4 11-2-1-3-3-3-3-5 7 0 12 6 12 7 0 11-8 5-13 0 3-2 4-2 4 1-5-2-11-2-11Z'},
    bolt: {path: 'm13 2-9 12h7l-1 8 10-13h-7l1-7Z'},
    trending_up: {path: 'm3 17 6-6 4 4 8-10m-7 0h7v7'},
    heart: {path: 'M20.8 4.6a5.5 5.5 0 0 0-7.8 0l-1 1-1-1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z'},
    checkmark: {path: 'm4 12 5 5L20 6'}
  };
  const defaultIcon = type => ({best_seller: 'trophy', popular: 'eye', rating: 'star'}[type] || 'flame');
  const customImages = new Map();
  function validCustomIcon(icon) {
    if (!object(icon) || !['image/png', 'image/svg+xml'].includes(icon.mime) || typeof icon.url !== 'string' || icon.url.length > 2048 || /["'\\\s]/.test(icon.url)) return false;
    try {
      const url = new URL(icon.url);
      return url.protocol === 'https:' && url.hostname === 'cdn.shopify.com' && !url.port && !url.username && !url.password && url.pathname.startsWith('/s/files/');
    } catch (_) { return false; }
  }
  function iconElement(choice) {
    const definition = icons[choice] || icons.flame;
    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    for (const [key, value] of Object.entries({viewBox: '0 0 24 24', 'aria-hidden': 'true', focusable: 'false', class: 'merchandising-badge__icon', fill: definition.fill ? 'currentColor' : 'none', stroke: 'currentColor', 'stroke-width': '1.8', 'stroke-linejoin': 'round'})) icon.setAttribute(key, value);
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', definition.path);
    icon.append(path);
    return icon;
  }
  function customReady(url, retry = false) {
    if (retry) customImages.delete(url);
    if (!customImages.has(url)) customImages.set(url, new Promise(resolve => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      const timer = setTimeout(() => resolve(false), 6000);
      image.onerror = () => { clearTimeout(timer); resolve(false); };
      image.onload = () => {
        clearTimeout(timer);
        try {
          const canvas = document.createElement('canvas'); canvas.width = canvas.height = 128;
          const context = canvas.getContext('2d', {willReadFrequently: true});
          context.drawImage(image, 0, 0, 128, 128);
          const pixels = context.getImageData(0, 0, 128, 128).data;
          resolve(pixels.some((value, index) => index % 4 === 3 && value > 0));
        } catch (_) { resolve(false); }
      };
      image.src = url;
    }));
    return customImages.get(url);
  }

  function validBadge(badge) {
    return badge === null || (object(badge) && allowedTypes.includes(badge.type) && object(badge.text)
      && ['en', 'fr'].every(lang => object(badge.text[lang])
        && ['plp', 'pdp', 'detail'].every(key => typeof badge.text[lang][key] === 'string')));
  }

  function validAppearance(style, catalog = fontCatalog) {
    return object(style) && ['background', 'foreground', 'gradient_end'].every(key => typeof style[key] === 'string' && /^#[0-9a-f]{6}$/i.test(style[key]))
      && typeof style.gradient_enabled === 'boolean' && Number.isInteger(style.gradient_angle)
      && style.gradient_angle >= 0 && style.gradient_angle <= 360
      && (!own(style, 'icon') || [...Object.keys(icons), 'custom', 'none'].includes(style.icon))
      && (!own(style, 'custom_icon') || validCustomIcon(style.custom_icon))
      && (!own(style, 'typography') || validTypography(style.typography, catalog));
  }

  function validPayload(data) {
    if (!object(data) || data.schema !== 1 || typeof data.enabled !== 'boolean'
      || typeof data.ready !== 'boolean' || typeof data.generation !== 'string'
      || !object(data.products) || !object(data.collections) || !object(data.appearance)) return false;
    if (own(data, 'retention') && data.retention !== 'until_replaced') return false;
    if (own(data, 'font_catalog') && !validFontCatalog(data.font_catalog)) return false;
    const catalog = data.font_catalog || {};
    if (!validAppearance(data.appearance, catalog)) return false;
    if (own(data, 'badge_appearances') && (!object(data.badge_appearances)
      || !allowedTypes.every(type => validAppearance(data.badge_appearances[type], catalog)))) return false;
    if (data.enabled && data.ready && (!Number.isFinite(Date.parse(data.metrics_at))
      || !Number.isFinite(Date.parse(data.expires_at)) || !usable(data))) return false;
    return Object.entries(data.products).every(([id, record]) => numericId(id) && object(record)
      && own(record, 'base') && own(record, 'direct') && validBadge(record.base) && validBadge(record.direct)
      && Array.isArray(record.collection_ids) && record.collection_ids.every(numericId)
      && object(record.collections) && Object.values(record.collections).every(validBadge));
  }

  async function fetchJson(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 6000);
    try {
      const response = await fetch(url, {credentials: 'omit', signal: controller.signal, ...options});
      if (!response.ok) throw new Error('Badge data unavailable');
      const data = await response.json();
      if (!validPayload(data)) throw new Error('Invalid badge snapshot');
      return data;
    } finally { clearTimeout(timeout); }
  }

  // An explicit empty card context must not inherit the surrounding PDP/PLP collection.
  // Older mounts that omitted the attribute retain their document-context behavior.
  const collectionContext = slot => own(slot.dataset, 'collectionId') ? slot.dataset.collectionId : config.collectionId;

  function resolve(data, slot) {
    if (!data.enabled || !data.ready) return null;
    const record = data.products[slot.dataset.productId];
    if (!record) return null; // A valid missing/no-badge entry is authoritative.
    const context = collectionContext(slot);
    if (context && own(record.collections, context)) return record.collections[context];
    if (context && record.collection_ids.includes(String(context))) return record.base;
    return slot.dataset.surface === 'pdp' ? record.direct : record.base;
  }

  function draw(slot, data) {
    const context = collectionContext(slot) || '';
    const key = [data?.generation || 'unavailable', slot.dataset.productId, context, slot.dataset.surface, locale].join(':');
    if (rendered.get(slot) === key) return;
    rendered.set(slot, key);
    const badge = data ? resolve(data, slot) : null;
    const style = badge ? (data.badge_appearances?.[badge.type] || data.appearance) : null;
    paint(slot, badge, style, slot.dataset.surface, locale, data?.font_catalog || {});
  }

  // Shared by the editor's illustrative previews and the storefront itself.
  function paint(slot, badge, style, surface, language, catalog = fontCatalog) {
    slot.replaceChildren();
    slot.hidden = !badge;
    if (!badge) return;
    slot.dataset.badgeType = badge.type;
    slot.style.setProperty('--merchandising-badge-background', style.gradient_enabled
      ? `linear-gradient(${style.gradient_angle}deg, ${style.background}, ${style.gradient_end})` : style.background);
    slot.style.setProperty('--merchandising-badge-foreground', style.foreground);
    applyTypography(slot, style.typography || defaultTypography, catalog);
    const choice = style.icon || defaultIcon(badge.type);
    slot.dataset.badgeIcon = choice;
    if (choice !== 'none') {
      const icon = iconElement(choice === 'custom' ? defaultIcon(badge.type) : choice);
      slot.append(icon);
      if (choice === 'custom' && validCustomIcon(style.custom_icon) && typeof CSS !== 'undefined'
        && (CSS.supports('mask-image', 'url("x")') || CSS.supports('-webkit-mask-image', 'url("x")'))) {
        const url = style.custom_icon.url;
        customReady(url).then(ready => {
          if (!ready || !slot.contains(icon)) return;
          const mask = document.createElement('span');
          mask.className = 'merchandising-badge__icon merchandising-badge__icon--custom';
          mask.setAttribute('aria-hidden', 'true');
          mask.style.setProperty('--merchandising-badge-icon', 'url(' + JSON.stringify(url) + ')');
          icon.replaceWith(mask);
        });
      }
    }
    const content = document.createElement('span');
    content.className = 'merchandising-badge__content';
    slot.append(content);
    const label = document.createElement('span');
    label.className = 'merchandising-badge__label';
    label.textContent = badge.text[language][surface === 'pdp' ? 'pdp' : 'plp'];
    content.append(label);
    const detail = badge.text[language].detail;
    if (detail && (surface === 'pdp' || badge.type === 'popular')) {
      const description = document.createElement('span');
      description.className = 'merchandising-badge__detail';
      description.textContent = detail;
      content.append(description);
    }
  }

  window.MerchandisingBadgePresentation = {paint, validAppearance, icons, iconElement, defaultIcon, validCustomIcon, customReady, fontCatalog, validTypography, defaultTypography};
  if (!configNode) return; // Admin previews never fetch badge data or record telemetry.

  function watchExpiry(data) {
    clearTimeout(expirationTimer);
    if (!data?.enabled || !data.ready || data.retention === 'until_replaced') return;
    expirationTimer = setTimeout(() => { asset = null; schedule(); }, Math.max(1, Date.parse(data.expires_at) - Date.now() + 1));
  }

  // One request per theme asset per document, shared by every initial and later product card.
  const assetReady = (async () => {
    try {
      const data = await fetchJson(config.assetUrl);
      asset = data;
      watchExpiry(data);
    } catch (_) { asset = null; }
  })();

  async function render() {
    await assetReady;
    const slots = Array.from(document.querySelectorAll(selector)).filter(slot => numericId(slot.dataset.productId));
    if (asset && usable(asset)) {
      slots.forEach(slot => draw(slot, asset));
      return;
    }
    const missing = [...new Set(slots.filter(slot => slot.getClientRects().length || !slot.parentElement?.hidden)
      .map(slot => slot.dataset.productId))].filter(id => !pending.has(id));
    for (let index = 0; index < missing.length; index += 100) {
      const ids = missing.slice(index, index + 100);
      let batch;
      try {
        const url = new URL(config.fallbackUrl);
        url.searchParams.set('theme_id', config.themeId);
        ids.forEach(id => url.searchParams.append('ids[]', id));
        batch = fetchJson(url.toString()).catch(() => null);
      } catch (_) { batch = Promise.resolve(null); }
      ids.forEach(id => pending.set(id, batch));
    }
    await Promise.all(slots.map(async slot => {
      const data = await (pending.get(slot.dataset.productId) || Promise.resolve(null));
      const fresh = data && usable(data);
      if (slot.isConnected) draw(slot, fresh ? data : null);
      if (fresh) watchExpiry(data);
    }));
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; render().catch(() => {}); });
  }

  new MutationObserver(changes => {
    if (changes.some(change => change.type === 'attributes' || [...change.addedNodes].some(node =>
      node.nodeType === 1 && (node.matches(selector) || node.querySelector(selector))))) schedule();
  }).observe(document.documentElement, {childList: true, subtree: true, attributes: true, attributeFilter: ['data-product-id', 'data-collection-id']});
  document.addEventListener('shopify:section:load', schedule);
  window.addEventListener('pageshow', schedule);
  schedule();

  // This independent telemetry path never blocks rendering and never tracks preview/editor traffic.
  const preview = config.preview || window.Shopify?.designMode || window.Shopify?.theme?.role !== 'main'
    || new URLSearchParams(location.search).has('preview_theme_id') || location.hostname.endsWith('.shopifypreview.com');
  if (config.surface !== 'pdp' || preview || !numericId(config.productId) || !crypto.randomUUID) return;
  const eventId = crypto.randomUUID();
  let sent = false;
  function recordView() {
    if (sent || window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== true) return;
    sent = true;
    const body = new URLSearchParams({event_id: eventId, product_id: String(config.productId),
      theme_id: String(config.themeId), analytics_consent: '1', preview: '0'});
    fetch(config.fallbackUrl + '/views', {method: 'POST', body, credentials: 'omit', keepalive: true}).catch(() => {});
  }
  document.addEventListener('visitorConsentCollected', recordView);
  document.addEventListener('trackingConsentAccepted', recordView);
  if (window.Shopify?.customerPrivacy) recordView();
  else if (window.Shopify?.loadFeatures) window.Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.1'}], error => { if (!error) recordView(); });
})();
